/**
 * Datasets API methods
 * Handles all dataset-related API calls
 */

import {
  extractArrayFromResponse,
  extractIdFromLocation,
  getBasePath,
  normalizeTimestamp,
  createNeedsRefetchError
} from './base';
import type { Dataset } from './types';

import axiosInstance from '~/common/helpers/axiosInstance';

/**
 * Create a new dataset
 */
export const createDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  data: { name: string }
): Promise<Dataset> => {
  // Validate required parameters
  if (!realm || !pipelineName || !connectorId) {
    throw new Error(
      `Missing required parameters: realm=${realm}, pipelineName=${pipelineName}, connectorId=${connectorId}`
    );
  }

  // Ensure version is valid
  const validVersion = version && version > 0 ? version : 1;
  const path = `${getBasePath(realm, pipelineName, validVersion, connectorId)}/datasets`;

  const response = await axiosInstance.post<Dataset>(path, data);

  // Handle case where API returns empty string but dataset was created (201 Created)
  const responseData: unknown = response.data;
  const isEmptyString =
    typeof responseData === 'string' && responseData.trim() === '';
  if (!responseData || isEmptyString) {
    const datasetId = extractIdFromLocation(
      response.headers?.location,
      'datasets'
    );

    if (!datasetId) {
      throw createNeedsRefetchError(
        'Dataset created but ID not available in response. Please refetch datasets.',
        data.name
      );
    }

    // Construct dataset object from request data and response
    // Use current timestamp in milliseconds for createdAt/updatedAt
    const now = Date.now();
    const constructedDataset: Dataset = {
      id: datasetId,
      name: data.name,
      connectorId,
      enabled: true,
      createdAt: now,
      updatedAt: now
    };

    return constructedDataset;
  }

  // Normalize timestamps to ensure they're numbers
  const dataset = response.data;
  return {
    ...dataset,
    createdAt: normalizeTimestamp(dataset.createdAt),
    updatedAt: normalizeTimestamp(dataset.updatedAt)
  };
};

/**
 * List all datasets
 */
export const getDatasets = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string
): Promise<Dataset[]> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets`;

  try {
    const response = await axiosInstance.get<unknown>(path);
    const dataArray = extractArrayFromResponse<unknown>(response.data);

    // Transform API response to match Dataset interface
    const transformed = dataArray.map((item: unknown) => {
      const dataset = item as Record<string, unknown>;
      return {
        id: dataset.id as string,
        name: dataset.name as string,
        pipelineId: (dataset.pipelineId || dataset.pipelineName) as
          | string
          | undefined,
        pipelineName: dataset.pipelineName as string | undefined,
        connectorId: dataset.connectorId as string,
        enabled: dataset.enabled as boolean,
        createdAt: normalizeTimestamp(dataset.createdAt),
        updatedAt: normalizeTimestamp(dataset.updatedAt),
        realmId: dataset.realmId as string | undefined,
        version: dataset.version as string | number | undefined,
        experiments: dataset.experiments as unknown[] | undefined,
        entityVersion: dataset.entityVersion as number | undefined
      } as Dataset;
    });

    return transformed;
  } catch (error: unknown) {
    // If error response has data, try to extract it
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response
    ) {
      const errorData = error.response.data;
      const dataArray = extractArrayFromResponse<unknown>(errorData);

      if (dataArray.length > 0) {
        return dataArray.map((item: unknown) => {
          const dataset = item as Record<string, unknown>;
          return {
            id: dataset.id as string,
            name: dataset.name as string,
            pipelineId: (dataset.pipelineId || dataset.pipelineName) as
              | string
              | undefined,
            pipelineName: dataset.pipelineName as string | undefined,
            connectorId: dataset.connectorId as string,
            enabled: dataset.enabled as boolean,
            createdAt: normalizeTimestamp(dataset.createdAt),
            updatedAt: normalizeTimestamp(dataset.updatedAt),
            realmId: dataset.realmId as string | undefined,
            version: dataset.version as string | number | undefined,
            experiments: dataset.experiments as unknown[] | undefined,
            entityVersion: dataset.entityVersion as number | undefined
          } as Dataset;
        });
      }
    }
    throw error;
  }
};

/**
 * Get a specific dataset
 */
export const getDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string
): Promise<Dataset> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}`;
  const response = await axiosInstance.get<Dataset>(path);

  // Normalize timestamps
  const dataset = response.data;
  return {
    ...dataset,
    createdAt: normalizeTimestamp(dataset.createdAt),
    updatedAt: normalizeTimestamp(dataset.updatedAt)
  };
};

/**
 * Update a dataset
 */
export const updateDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string,
  data: Partial<Dataset>
): Promise<Dataset> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}`;
  const response = await axiosInstance.patch<Dataset>(path, data);

  // Normalize timestamps
  const dataset = response.data;
  return {
    ...dataset,
    createdAt: normalizeTimestamp(dataset.createdAt),
    updatedAt: normalizeTimestamp(dataset.updatedAt)
  };
};

/**
 * Enable a dataset
 */
export const enableDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string
): Promise<Dataset> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}/enable`;
  const response = await axiosInstance.patch<Dataset>(path);

  // Normalize timestamps
  const dataset = response.data;
  return {
    ...dataset,
    createdAt: normalizeTimestamp(dataset.createdAt),
    updatedAt: normalizeTimestamp(dataset.updatedAt)
  };
};

/**
 * Delete a dataset
 */
export const deleteDataset = async (
  realm: string,
  pipelineName: string,
  version: number,
  connectorId: string,
  datasetId: string
): Promise<void> => {
  const path = `${getBasePath(realm, pipelineName, version, connectorId)}/datasets/${encodeURIComponent(datasetId)}`;
  await axiosInstance.delete(path);
};
